import React from 'react';
import { observer, inject } from 'mobx-react';
import {
  Grid,
  Paper,
  Button,
  Table,
  TableHead,
  TableBody,
  TableCell,
  TableRow,
  Radio,
  IconButton,
  withStyles,
} from '@material-ui/core';

import Swal from 'sweetalert2';
import { withTranslation } from 'react-i18next';
import MainOrganizationForm from './Dialog/MainOrganizationForm';
import WarningForm from './Dialog/WarningForm';
import { clearLocalStorage } from '../../../utils/common';

const errMessage = {
  required: 'required_field',
  needsLength: 'this_field_needs_length',
  invalidField: 'this_field_is_invalid',
};

class MainOrganization extends React.PureComponent {
  constructor(props) {
    super(props);
    this.state = {
      open: false,
      dialogWarningForm: {
        id: null,
        name: null,
        open: false,
      },
      deletedId: null,
      data: {
        name: '',
        contactPerson: '',
        email: '',
        phoneNumber: '',
      },
      errorField: {
        name: {
          flag: false,
          msg: '',
        },
        email: {
          flag: false,
          msg: '',
        },
        phoneNumber: {
          flag: false,
          msg: '',
        },
      },
    };
  }

  componentDidMount() {
    this.props.locations.getLocationList(0, 0);
    this.props.wizard.resetNavigation();
  }

  onClickEdit(id) {
    const { locations } = this.props.locations.toJS();
    const found = locations.data.find((item) => item.id === id);
    if (found) {
      this.setState({
        open: true,
        data: {
          id: found.id,
          name: found.name,
          contactPerson: found.contactPerson,
          email: found.email,
          phoneNumber: found.phoneNumber,
        },
      });
    }
  }

  handleChange = (e) => {
    const { name, value } = e.target;
    this.clearErrorField(name);
    this.setState({
      data: {
        ...this.state.data,
        [name]: value,
      },
    });
  };

  validate() {
    const { t } = this.props;
    const { data } = this.state;
    let checkName = !data.name ? true : false;

    // validate phone number
    let checkPhoneNumber = false;
    let msgPhoneNumber = '';
    if (data.phoneNumber && data.phoneNumber.length < 10) {
      checkPhoneNumber = true;
      msgPhoneNumber = t(errMessage.needsLength).replace('x', 10);
    }

    // validate email
    let checkEmail = false;
    let msgEmail = '';
    if (data.email) {
      const regx = new RegExp(
        '^(([^<>()[\\]\\.,;:\\s@\\"]+(\\.[^<>()[\\]\\.,;:\\s@\\"]+)*)|(\\".+\\"))@((\\[[0-9]{1,3}\\.[0-9]{1,3}\\.[0-9]{1,3}\\.[0-9]{1,3}\\])|(([a-zA-Z\\-0-9]+\\.)+[a-zA-Z]{2,}))$',
      );
      if (regx.test(data.email)) {
        checkEmail = false;
        msgEmail = '';
      } else {
        checkEmail = true;
        msgEmail = t(errMessage.invalidField);
      }
    }

    this.setState({
      errorField: {
        name: {
          flag: checkName,
          msg: t(errMessage.required),
        },
        phoneNumber: {
          flag: checkPhoneNumber,
          msg: msgPhoneNumber,
        },
        email: {
          flag: checkEmail,
          msg: msgEmail,
        },
      },
    });
    return !(checkName || checkPhoneNumber || checkEmail);
  }

  clearErrorField(key) {
    if (key === 'name') {
      this.setState({
        ...this.state,
        errorField: {
          ...this.state.errorField,
          name: { flag: false, msg: '' },
        },
      });
    }
    if (key === 'phoneNumber') {
      this.setState({
        ...this.state,
        errorField: {
          ...this.state.errorField,
          phoneNumber: {
            flag: false,
            msg: '',
          },
        },
      });
    }
    if (key === 'email') {
      this.setState({
        ...this.state,
        errorField: {
          ...this.state.errorField,
          email: {
            flag: false,
            msg: '',
          },
        },
      });
    }
  }

  handleSubmitForm = async () => {
    const { data } = this.state;
    if (this.validate()) {
      if (data.id) {
        this.handleUpdate(data);
      } else {
        this.handleCreate(data);
      }
    }
  };

  handleCreate = async (data) => {
    const { t } = this.props;
    try {
      const result = await this.props.locations.createLocation(data);
      if (result.statusCode === 200) {
        this.handleCloseForm();
      }
    } catch (e) {
      if (e.statusCode === 401) {
        clearLocalStorage();
        window.location.href = '/login';
      } else if (e.statusCode === 400) {
        const msg = e.error.message || `${t('failed_to_create')}`;
        Swal.fire({ html: `<h4>${msg}`, type: 'error' });
      } else {
        Swal.fire({ html: `<h4>${t('failed_to_create')}`, type: 'error' });
      }
    }
  };

  handleUpdate = async (data) => {
    const { t } = this.props;
    try {
      const result = await this.props.locations.updateLocation(data);
      if (result.statusCode === 200) {
        this.handleCloseForm();
      }
    } catch (e) {
      if (e.statusCode === 401) {
        clearLocalStorage();
        window.location.href = '/login';
      } else if (e.statusCode === 400) {
        const msg = e.error.message || `${t('failed_to_update')}`;
        Swal.fire({ html: `<h4>${msg}`, type: 'error' });
      } else {
        Swal.fire({ html: `<h4>${t('failed_to_update')}`, type: 'error' });
      }
    }
  };

  handleDelete = async () => {
    const { dialogWarningForm } = this.state;
    const { t } = this.props;
    if (dialogWarningForm.id) {
      try {
        await this.props.locations.deleteLocation(dialogWarningForm.id);
        await this.props.user.updateLocationPermission([dialogWarningForm.id]);
        this.handleCloseWarningForm();
      } catch (e) {
        if (e.statusCode === 401) {
          clearLocalStorage();
          window.location.href = '/login';
        } else if (e.statusCode === 400) {
          const msg = e.error.message || `${t('failed_to_unassign')}`;
          Swal.fire({ html: `<h4>${msg}`, type: 'error' });
        } else {
          Swal.fire({ html: `<h4>${t('failed_to_remove')}`, type: 'error' });
        }
      }
    }
  };

  handleOpendWarningForm = (id, name) => {
    this.setState({
      dialogWarningForm: {
        id,
        name,
        open: true,
      },
    });
  };

  handleCloseWarningForm = () => {
    this.setState({
      dialogWarningForm: {
        id: null,
        name: null,
        open: false,
      },
    });
  };

  handleNext = () => {
    const { activeStep } = this.props.wizard.toJS();
    this.props.wizard.nextStep(activeStep);

    const { locationName } = this.props.locations.toJS();
    const data = [
      {
        wizardStep: 0,
        wizardPage: 'MainOrganization',
        wizardName: locationName,
      },
    ];
    this.props.wizard.setNavigation(data);
  };

  handleOpenForm = () => {
    this.setState({ open: true });
  };

  handleCloseForm = () => {
    this.setState({ open: false });
  };

  handleSelect = (locationId, name) => {
    this.props.locations.setAction('locationId', locationId);
    this.props.locations.setAction('locationName', name);
  };

  render() {
    const { classes, t } = this.props;
    const { locations, locationId } = this.props.locations.toJS();
    return (
      <div>
        <Grid container>
          <Grid item xs={12} sm={12} md={8}>
            <div className={classes.textHeader}>{t('wizard_step_main_organization')}</div>
          </Grid>
          <Grid item xs={12} sm={12} md={4} className={classes.wrapButton}>
            <Button
              id='create'
              variant='contained'
              style={{
                backgroundColor: '#009D9A',
                color: '#fff',
                borderRadius: 8,
              }}
              onClick={this.handleOpenForm}
            >
              <div style={{ marginRight: 5 }}>
                <svg width='24' height='24' viewBox='0 0 24 24' style={{ fill: '#fff' }}>
                  <use xlinkHref={`/images/menus-v1.svg#add-circle`} />
                </svg>
              </div>
              {t('wizard_main_organization_create')}
            </Button>
          </Grid>
        </Grid>

        <Grid item xs={12} sm={12} md={12} style={{ marginTop: 20 }}>
          <Paper className={classes.paperTable}>
            <div style={{ overflow: 'auto', maxHeight: '543px' }}>
              <Table>
                <TableHead>
                  <TableRow style={{ border: 'none' }}>
                    <CustomTableCell style={{ borderTopLeftRadius: '8px' }} className={classes.tableCellRadio} />
                    <CustomTableCell>{t('wizard_main_organization_name')}</CustomTableCell>
                    <CustomTableCell>{t('wizard_main_organization_no_of_tenant')}</CustomTableCell>
                    <CustomTableCell style={{ borderTopRightRadius: '8px' }}>{t('action')}</CustomTableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {(locations.data || []).map((location, i) => (
                    <TableRow key={i}>
                      <CustomTableCell component='th' scope='row' className={classes.tableCellRadio}>
                        <Grid container alignItems='center'>
                          <Radio
                            id={`radio-${location.id}`}
                            checked={locationId === location.id}
                            value={location.id}
                            name='locationId'
                            color='primary'
                            onChange={() => this.handleSelect(location.id, location.name)}
                          />
                        </Grid>
                      </CustomTableCell>
                      <CustomTableCell component='th' scope='row'>
                        {location.name}
                      </CustomTableCell>
                      <CustomTableCell component='th' scope='row'>
                        {location.tenants.length}
                      </CustomTableCell>
                      <CustomTableCell component='th' scope='row'>
                        <Grid container alignItems='center'>
                          <IconButton
                            id={`edit-${location.id}`}
                            onClick={() => this.onClickEdit(location.id)}
                            className={classes.btnIcon}
                          >
                            <svg width='24' height='24' viewBox='0 0 24 24'>
                              <use xlinkHref={`/images/menus-v1.svg#pencil`} />
                            </svg>
                          </IconButton>
                          <IconButton
                            id={`delete-${location.id}`}
                            className={classes.btnIcon}
                            disabled={location.tenants.length > 0 ? true : false}
                            onClick={() => this.handleOpendWarningForm(location.id, location.name)}
                          >
                            <svg width='24' height='24' viewBox='0 0 16 16'>
                              <use xlinkHref={`/images/menus-v1.svg#trash`} />
                            </svg>
                          </IconButton>
                        </Grid>
                      </CustomTableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          </Paper>
        </Grid>
        <Grid container style={{ marginTop: 20, justifyContent: 'end' }}>
          <Button
            id='continue'
            variant='contained'
            color='primary'
            onClick={this.handleNext}
            className={classes.btnPrimary}
            disabled={!locationId}
          >
            {t('wizard_continue')}
          </Button>
        </Grid>

        <MainOrganizationForm
          open={this.state.open}
          data={this.state.data}
          errorField={this.state.errorField}
          handleChange={this.handleChange}
          handleSubmitForm={this.handleSubmitForm}
          handleCloseForm={this.handleCloseForm}
        />

        <WarningForm
          open={this.state.dialogWarningForm.open}
          handleSubmitForm={this.handleDelete}
          handleCloseForm={this.handleCloseWarningForm}
          cancelButtonText={t('no')}
          confirmButtonText={t('confirm')}
          headerTitle={t('wizard_main_organization_delete')}
          subTitle={t('wizard_main_organization_delete_subtitle').replace('x', this.state.dialogWarningForm.name)}
        />
      </div>
    );
  }
}

const CustomTableCell = withStyles((theme) => ({
  head: {
    backgroundColor: 'rgba(230, 244, 243, 0.5)',
    color: theme.palette.text.header,
    border: 'none',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
    [theme.breakpoints.down('md')]: {
      maxWidth: '200px',
    },
    [theme.breakpoints.down('sm')]: {
      maxWidth: '50px',
    },
  },
  body: {
    fontSize: 16,
    borderTop: '1px solid #B8CFC3',
    borderBottom: 'none',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
    [theme.breakpoints.down('md')]: {
      maxWidth: '200px',
    },
    [theme.breakpoints.down('sm')]: {
      maxWidth: '50px',
    },
  },
}))(TableCell);

const styles = (theme) => ({
  textHeader: {
    color: theme.palette.text.header,
    fontWeight: 500,
    fontSize: 30,
  },
  wrapButton: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'left',
    [theme.breakpoints.up('md')]: {
      justifyContent: 'right',
    },
  },
  btnPrimary: {
    width: 200,
    borderRadius: '4px',
  },
  paperTable: {
    boxShadow: 'none',
    border: '1px solid #B8CFC3',
    borderRadius: '8px',
  },
  tableCellRadio: {
    width: '50px',
    padding: 4,
  },
  btnIcon: {
    marginRight: 10,
    borderRadius: 4,
    padding: 4,
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    cursor: 'pointer',
    fill: theme.palette.primary.main,
    backgroundColor: theme.palette.primary.bg,
    '&:hover': {
      fill: theme.palette.primary.bg,
      backgroundColor: theme.palette.primary.main,
    },
    '&:disabled': {
      fill: theme.palette.disable.main,
      backgroundColor: theme.palette.disable.light,
    },
    [theme.breakpoints.down('md')]: {
      marginRight: 5,
    },
  },
});

export const page = inject('wizard', 'locations', 'user')(observer(MainOrganization));
export const translation = withTranslation()(page);
export default withStyles(styles, { withTheme: true })(translation);
